/* 0.49.25 - Moving between pages without reloading the site.
 *
 * Links written as plain addresses (a button's URL, a picture's link,
 * a link in text, a guide card, the footer) made the browser load the
 * whole site again on every click: the start page, the scripts, the
 * languages and translations, the settings, the menu, the layout and
 * who is signed in, about a dozen calls in two rounds before the next
 * page could even be asked for. On a real server each move took a
 * second or more and the screen went white in between.
 *
 * This catches a click on a link to another page of this site and
 * hands it to the router, the way the menu's own links already work:
 * only the new page is fetched. Left alone (the browser does what it
 * always did):
 *   - a click with Ctrl, Cmd, Shift or Alt, or not the main button
 *   - a link opening in a new tab, a download, a link marked
 *     data-reload, or one another handler already took
 *   - another site, mail and phone links
 *   - files and server addresses (/api/, /media/, anything with a
 *     file extension) and the admin area
 *   - a #link to a part of the same page (scrolled by useHashScroll)
 *
 * Code that moved by setting window.location (an information card's
 * Edit and action, Back links) calls goTo() instead.
 *
 * Off on the admin address, where pages guard unsaved changes on a
 * full reload. */
import { useEffect } from 'react';
import { useNavigate, useLocation, useNavigationType } from 'react-router-dom';
import { appPath, withBase } from '../lib/basePath';
import { isAdminHost } from '../utils/adminHost';
import { prepareMove } from '../lib/valueHandover';

let navigateRef = null;

const FILE_RE = /\.[A-Za-z0-9]{1,6}$/;
const SKIP_PREFIX = ['/api/', '/media/', '/uploads/', '/static/', '/assets/', '/admin'];

/** The router address for `href`, or null when it should load normally. */
export function spaTarget(href) {
  if (typeof window === 'undefined' || !href) return null;
  let url;
  try { url = new URL(href, window.location.href); } catch (_e) { return null; }
  if (url.origin !== window.location.origin) return null;
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
  const path = appPath(url.pathname);
  if (SKIP_PREFIX.some(p => path.startsWith(p.endsWith('/') ? p : `${p}/`) || path === p.replace(/\/$/, ''))) return null;
  if (FILE_RE.test(path)) return null;
  if (url.pathname === window.location.pathname && url.search === window.location.search) {
    // Same page: a #part is scrolled to by the page itself; the very
    // same address does nothing new either way.
    if (url.hash) return null;
  }
  return `${path}${url.search}${url.hash}`;
}

/* 0.49.54 - An address with values (?training=t15) to a page or a flow
   that takes them through the server: the values go to the server and
   the clean address opens (lib/valueHandover.js). */
function move(navigate, to) {
  if (to.indexOf('?') < 0) { navigate(to); return; }
  prepareMove(to).then(({ to: next, state }) => navigate(next, state ? { state } : undefined))
    .catch(() => navigate(to));
}

/** Go to an address of this site without reloading it, when possible. */
export function goTo(url) {
  const to = spaTarget(withBase(String(url || '')));
  if (to && navigateRef && !isAdminHost()) { move(navigateRef, to); return; }
  window.location.href = withBase(url);
}

export default function SpaLinks() {
  const navigate = useNavigate();
  const location = useLocation();
  const navType = useNavigationType();
  const active = !isAdminHost();

  useEffect(() => {
    if (!active) return undefined;
    navigateRef = navigate;
    return () => { if (navigateRef === navigate) navigateRef = null; };
  }, [navigate, active]);

  useEffect(() => {
    if (!active) return undefined;
    const onClick = (ev) => {
      if (ev.defaultPrevented || ev.button !== 0) return;
      if (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
      const a = ev.target && ev.target.closest ? ev.target.closest('a[href]') : null;
      if (!a) return;
      const target = a.getAttribute('target');
      if (target && target !== '_self') return;
      if (a.hasAttribute('download') || a.hasAttribute('data-reload')) return;
      const to = spaTarget(a.getAttribute('href'));
      if (!to) return;
      ev.preventDefault();
      move(navigate, to);
    };
    // Bubbling, so an element's own handler runs first and can take it.
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [navigate, active]);

  // A new page starts at its top, as after a reload. Back and Forward
  // (POP) keep the browser's own position, and a #part is scrolled to
  // by the page.
  useEffect(() => {
    if (!active || navType === 'POP' || location.hash) return;
    window.scrollTo(0, 0);
  }, [location.pathname, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}
